import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

/**
 * Pure-Tailwind searchable combobox — replaces TomSelect system-wide.
 * The dropdown panel is portaled to document.body so it escapes any
 * overflow:hidden / overflow:auto ancestor (DataTable, modals, etc.).
 */
const AppSelect = React.forwardRef(function AppSelect(
  {
    options: optionsProp,
    value,
    onChange,
    placeholder = 'Select…',
    label,
    required = false,
    error = false,
    helperText,
    disabled = false,
    searchable = true,
    clearable = false,
    fullWidth = true,
    wrapperClassName = '',
    className = '',
    filterBarMatch = false,
    name,
    children,
    // Optional remote-search hook. When provided, the typed query is forwarded
    // to the parent (debounced upstream) so it can fetch options that were
    // never loaded into `options`. Local substring filtering still applies on
    // top of whatever options end up present.
    onSearchChange,
    loading = false,
  },
  _ref
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const options = useMemo(() => {
    if (Array.isArray(optionsProp)) return optionsProp;
    const out = [];
    React.Children.forEach(children, (child) => {
      if (child && child.type === 'option') {
        out.push({
          value: child.props.value ?? '',
          label: child.props.children ?? child.props.value ?? '',
          disabled: !!child.props.disabled,
        });
      }
    });
    return out;
  }, [optionsProp, children]);

  const selectedOption = options.find((o) => String(o.value) === String(value ?? ''));
  const displayLabel = selectedOption?.label ?? '';

  const filtered = useMemo(
    () =>
      query
        ? options.filter((o) =>
            String(o.label ?? o.value)
              .toLowerCase()
              .includes(query.toLowerCase())
          )
        : options,
    [options, query]
  );

  // Compute portal panel position — opens upward if not enough space below.
  const PANEL_MAX_H = 300;
  const updateCoords = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;
    const openUp = spaceBelow < PANEL_MAX_H && spaceAbove > spaceBelow;
    const maxHeight = Math.min(PANEL_MAX_H, (openUp ? spaceAbove : spaceBelow) - 8);
    setCoords({
      left: rect.left + window.scrollX,
      width: rect.width,
      maxHeight,
      top: openUp ? undefined : rect.bottom + window.scrollY + 4,
      bottom: openUp ? window.innerHeight - rect.top - window.scrollY + 4 : undefined,
      openUp,
    });
  }, []);

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open, updateCoords]);

  useEffect(() => {
    if (!open) return;
    const handleOutside = (e) => {
      const inTrigger = containerRef.current?.contains(e.target);
      const inPanel = panelRef.current?.contains(e.target);
      if (!inTrigger && !inPanel) {
        setOpen(false);
        setQuery('');
      }
    };
    const handleReposition = () => updateCoords();
    document.addEventListener('mousedown', handleOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [open, updateCoords]);

  useEffect(() => {
    if (open && searchable) {
      setTimeout(() => searchRef.current?.focus(), 20);
    }
  }, [open, searchable]);

  const handleSelect = (opt) => {
    if (opt.disabled) return;
    if (onChange) onChange(opt.value);
    setOpen(false);
    setQuery('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    if (onChange) onChange('');
  };

  const hasError = !!error;
  const effectiveHelperText = helperText || (typeof error === 'string' && error ? error : null);

  const triggerBase = filterBarMatch
    ? 'h-8 rounded-[10px] border border-[#E9EDF6] bg-[#FAFBFE]'
    : 'h-9 rounded-lg border border-[#d0d5dd] bg-white';
  const triggerFocus = hasError
    ? 'border-[#EF4444] focus:ring-[#EF4444]/15'
    : 'hover:border-slate-300 focus:ring-[#0061B2]/15';

  // Portal panel — rendered at document.body level to escape any overflow ancestor
  const panel = open && coords
    ? createPortal(
        <div
          ref={panelRef}
          style={{
            position: 'fixed',
            left: coords.left,
            width: coords.width,
            minWidth: 200,
            maxHeight: coords.maxHeight,
            zIndex: 9999,
            ...(coords.openUp
              ? { bottom: coords.bottom }
              : { top: coords.top }),
          }}
          className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
        >
          {searchable && (
            <div className="border-b border-slate-100 px-3 py-2">
              <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-1.5 transition-all focus-within:border-[#0061B2] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#0061B2]/15">
                <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5 shrink-0 text-slate-400">
                  <path fillRule="evenodd" d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z" clipRule="evenodd" />
                </svg>
                <input
                  ref={searchRef}
                  type="text"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    if (onSearchChange) onSearchChange(e.target.value);
                  }}
                  placeholder="Search…"
                  className="flex-1 bg-transparent text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none"
                />
                {query && (
                  <button
                    type="button"
                    onClick={() => {
                      setQuery('');
                      if (onSearchChange) onSearchChange('');
                    }}
                    className="shrink-0 text-slate-400 hover:text-slate-600"
                  >
                    <svg viewBox="0 0 20 20" fill="currentColor" className="h-3.5 w-3.5">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          )}
          <ul className="overflow-y-auto py-1" style={{ maxHeight: coords.maxHeight - (searchable ? 56 : 0) }}>
            {filtered.length === 0 ? (
              <li className="px-4 py-3 text-center text-xs text-slate-400">
                {loading ? 'Searching…' : 'No options found'}
              </li>
            ) : (
              filtered.map((opt, idx) => {
                const isSelected = String(opt.value) === String(value ?? '');
                return (
                  <li key={`${opt.value}-${idx}`}>
                    <button
                      type="button"
                      disabled={!!opt.disabled}
                      onClick={() => handleSelect(opt)}
                      className={[
                        'flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors',
                        opt.disabled ? 'pointer-events-none opacity-40' : 'cursor-pointer',
                        isSelected
                          ? 'bg-[#0061B2]/5 font-medium text-[#0061B2]'
                          : 'text-slate-700 hover:bg-slate-50',
                      ].join(' ')}
                    >
                      <span className="flex-1 truncate">{opt.label ?? opt.value}</span>
                      {isSelected && (
                        <svg viewBox="0 0 20 20" fill="currentColor" className="h-4 w-4 shrink-0 text-[#0061B2]">
                          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>,
        document.body
      )
    : null;

  return (
    <div className={`${fullWidth ? 'w-full' : ''} ${wrapperClassName}`} ref={containerRef}>
      {label && (
        <label
          className={
            filterBarMatch
              ? 'mb-1.5 block text-xs font-semibold text-[#212B36]'
              : 'mb-1 block text-[13px] font-medium text-[#212B36]'
          }
        >
          {label}
          {required && <span className="ml-0.5 text-[#EF4444]">*</span>}
        </label>
      )}

      <div ref={triggerRef} className={`relative ${disabled ? 'pointer-events-none opacity-50' : ''}`}>
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen((v) => !v)}
          className={`${triggerBase} ${triggerFocus} ${className}
            flex w-full items-center justify-between gap-2 px-3 text-sm
            focus:outline-none focus:ring-2 transition-colors`}
        >
          <span className={`flex-1 truncate text-left ${displayLabel ? 'text-[#212B36]' : 'text-[#9aa3ab]'}`}>
            {displayLabel || placeholder}
          </span>
          <span className="flex shrink-0 items-center gap-1">
            {clearable && displayLabel && (
              <span
                role="button"
                tabIndex={0}
                onClick={handleClear}
                onKeyDown={(e) => e.key === 'Enter' && handleClear(e)}
                className="text-[#9aa3ab] hover:text-[#EF4444] focus:outline-none"
                aria-label="Clear"
              >
                ×
              </span>
            )}
            <svg
              viewBox="0 0 20 20"
              fill="currentColor"
              className={`h-4 w-4 text-slate-400 transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
            >
              <path
                fillRule="evenodd"
                d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </span>
        </button>
      </div>

      {panel}

      {effectiveHelperText && (
        <p className={`mt-1 text-xs ${hasError ? 'text-[#EF4444]' : 'text-[#637381]'}`}>
          {effectiveHelperText}
        </p>
      )}
    </div>
  );
});

AppSelect.displayName = 'AppSelect';
export default AppSelect;
